/**
 * /analytics — the network measurement dashboard (NETWORK_METRICS_AUTOMATION_PLAN
 * §7 / §7a, operator rulings D10–D14, 2026-09-15).
 *
 * The layout IS the guidance doc, in order: the site filter (scopes the whole
 * page); the headline row on its FIXED windows; then the range row, which
 * scopes only the charts and tables under it (operator 2026-09-22 — a range
 * row above the headline read as if it changed the tiles); leading beside lagging; per-site
 * first with the network roll-up as an option, never the default; two zones
 * visibly divided with the visibility tag on every card; every figure
 * stating its basis; the trend anchored to the 2026-07-15 launch. Gated
 * sources render as "awaiting source" cards naming their gate (D11).
 *
 * v1 has one section, Network measurement; AI cost and editorial sections
 * attach here later (D13).
 */
import { useMemo, useState } from 'react';
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Download } from 'lucide-react';
import {
  downloadNetworkCsv, getMetricCatalog, getMetricCoverage, getMetricSeries,
  type MetricDefinition, type SeriesResponse,
} from '../lib/api/analytics.js';
import {
  type DailyShape, type RangePreset, LAUNCH_ANCHOR, SITE_LABEL, SITE_ORDER, addDays, combineSites,
  coverageByFamily, latestBySite, rangeForPreset, rowsAsSeriesByDimension, rowsForDimensionValue, shapeDaily, sliceDays, sparkline, subtractShapes, topDimension, trailingTotal, trendOf, valueAsOf, windowNoTrendReason, windowTrend, ymd,
} from '../lib/analyticsShape.js';
import { StatTile } from '../components/analytics/StatTile.js';
import { LinkedInFacets } from '../components/analytics/LinkedInFacets.js';
import { CoverageTable } from '../components/analytics/CoverageTable.js';
import { TrafficQuality } from '../components/analytics/TrafficQuality.js';
import { PipelineHealth } from '../components/analytics/PipelineHealth.js';
import { PartnerClicks } from '../components/analytics/PartnerClicks.js';
import { StandingFollowers } from '../components/analytics/StandingFollowers.js';
import { SeriesChart } from '../components/analytics/SeriesChart.js';
import { CrawlerTable } from '../components/analytics/CrawlerTable.js';

/** Every daily series the page reads. Order is irrelevant; keys are the catalog's. */
const SERIES_KEYS = [
  'rum.pageviews', 'rum.visits', 'rum.pageviews_au', 'cf.ai_crawler.requests', 'cf.zone.pageviews',
  'social.impressions', 'social.engagement', 'internal.articles_published', 'internal.company_hubs',
  'gsc.branded.impressions', 'gsc.clicks', 'gsc.impressions', 'internal.company_hubs_network',
  'li.followers.gained', 'li.followers.total', 'li.shares.impressions', 'li.page.views',
  'cf.ai_assisted.reads', 'rum.crawler_suspect.pageviews',
] as const;

const PRESETS: Array<{ key: RangePreset; label: string }> = [
  { key: 'launch', label: 'Since launch' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: '12m', label: 'Last 12 months' },
];

/** The day ShapBot and ExaSearchBot entered the AI registry (B-12). The step
 *  in the series on this date is a classification change, not traffic. */
const REGISTRY_WIDENED = '2026-09-21';

const SOURCE: Record<string, string> = {
  cloudflare: 'Cloudflare',
  buffer: 'Buffer',
  internal: 'Assayer',
  gsc: 'Search Console',
  linkedin: 'LinkedIn',
};

function sourceLine(def: MetricDefinition | undefined, through: string | null): string {
  const src = def ? (SOURCE[def.collector] ?? def.collector) : 'source';
  const detail = def?.metric_key.startsWith('rum.') ? ' beacon' : def?.metric_key.startsWith('cf.zone') ? ' zone' : def?.metric_key.startsWith('cf.ai') ? ' adaptive' : def?.metric_key.startsWith('gsc.') ? ' search analytics' : def?.metric_key.startsWith('li.') ? ' Page analytics' : '';
  return through ? `${src}${detail} · through ${through}` : `${src}${detail}`;
}

function perSiteLine(bySite: Array<{ site: string; value: number | null }>): string {
  return bySite
    .filter((b) => b.value != null)
    .map((b) => `${SITE_LABEL[b.site] ?? b.site} ${Math.round(b.value as number).toLocaleString()}`)
    .join(' · ');
}

export function AnalyticsPage() {
  const today = ymd(new Date());
  const [preset, setPreset] = useState<RangePreset>('launch');
  const [site, setSite] = useState<string>('all');
  const [combine, setCombine] = useState(false);

  const range = useMemo(() => rangeForPreset(preset, today), [preset, today]);
  // One fetch serves the charts (the selected range) AND the headline tiles
  // (trailing 30 days off the latest measured day), so the numbers agree.
  // Twice the longest window: the trend badges compare each window with the
  // one before. 65, not 60: a 30-day trend compares against the 30 days before it, and the
  // data runs through yesterday or the day before (daily 01:00Z sweep), so a
  // 60-day fetch left the comparison window a day or two short and the badge
  // never showed (2026-10-16, AI crawlers 30-day view).
  const fetchFrom = range.from < addDays(range.to, -65) ? range.from : addDays(range.to, -65);
  const sites = site === 'all' ? undefined : [site];

  const catalog = useQuery({ queryKey: ['analytics-catalog'], queryFn: getMetricCatalog, staleTime: 5 * 60_000 });
  const coverage = useQuery({ queryKey: ['analytics-coverage'], queryFn: getMetricCoverage, staleTime: 10 * 60_000 });
  const covByMetric = useMemo(() => new Map((coverage.data ?? []).map((m) => [m.metric_key, m])), [coverage.data]);
  const covFamilies = useMemo(() => coverageByFamily(coverage.data ?? []), [coverage.data]);
  // When each chart's metric began — earlier days are shaded "not collected".
  const cov = (k: string) => ({
    collectedFrom: covByMetric.get(k)?.earliest ?? null,
    activityFrom: covByMetric.get(k)?.first_activity ?? null,
  });
  const defs = useMemo(() => {
    const m = new Map<string, MetricDefinition>();
    for (const d of catalog.data?.metrics ?? []) m.set(d.metric_key, d);
    return m;
  }, [catalog.data]);

  const series = useQueries({
    queries: SERIES_KEYS.map((metric) => ({
      queryKey: ['analytics-series', metric, fetchFrom, range.to, site],
      queryFn: () => getMetricSeries({ metric, sites, dateFrom: fetchFrom, dateTo: range.to }),
      placeholderData: keepPreviousData,
      staleTime: 60_000,
    })),
  });
  const crawlerFrom = addDays(range.to, -6);
  const crawlers = useQuery({
    queryKey: ['analytics-crawlers', crawlerFrom, range.to, site],
    queryFn: () => getMetricSeries({ metric: 'cf.ai_crawler.requests', sites, dateFrom: crawlerFrom, dateTo: range.to, dimension: 'crawler' }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // Top queries over the trailing four weeks: the per-query breakdown is
  // stored for the top 100 a day, so a window sum is a floor, like the total.
  // X impressions live under social.impressions/platform=twitter. LinkedIn
  // impressions come from LinkedIn's own API (li.shares.impressions), not
  // Buffer — Buffer under-reported them 29–50% (plan §10a, ruling R1).
  const platform = useQuery({
    queryKey: ['analytics-social-platform', fetchFrom, range.to, site],
    queryFn: () => getMetricSeries({ metric: 'social.impressions', sites, dateFrom: fetchFrom, dateTo: range.to, dimension: 'platform' }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  // AI-assisted reads by category (answer / agent action / notebook / read
  // aloud), trailing 30 days — the card's breakdown line (2026-10-06).
  const aiReadsFrom = addDays(range.to, -29);
  const aiReadsCats = useQuery({
    queryKey: ['analytics-ai-reads-category', aiReadsFrom, range.to, site],
    queryFn: () => getMetricSeries({ metric: 'cf.ai_assisted.reads', sites, dateFrom: aiReadsFrom, dateTo: range.to, dimension: 'category' }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const xShape = useMemo(() => (platform.data
    ? shapeDaily(rowsForDimensionValue(platform.data.rows, 'twitter'), fetchFrom, range.to, 'social.impressions')
    : undefined), [platform.data, fetchFrom, range.to]);

  // Median impressions per LinkedIn post (R6): rolling 30 days, stored under
  // dimension platform=linkedin. Never summed across sites — a median is not
  // additive, so the network roll-up is deliberately not applied to it.
  const median = useQuery({
    queryKey: ['analytics-median-post', fetchFrom, range.to, site],
    queryFn: () => getMetricSeries({ metric: 'social.median_impressions_per_post', sites, dateFrom: fetchFrom, dateTo: range.to, dimension: 'platform' }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const medianShape = useMemo(() => (median.data
    ? sliceDays(shapeDaily(rowsForDimensionValue(median.data.rows, 'linkedin'), fetchFrom, range.to, 'social.median_impressions_per_post'), range.from, range.to)
    : undefined), [median.data, fetchFrom, range.from, range.to]);

  // Beacon traffic quality (mig 389) — internal reference only; the public
  // page-views figure is unchanged (operator, 2026-09-29).
  const quality = useQuery({
    queryKey: ['analytics-quality', fetchFrom, range.to, site],
    queryFn: () => getMetricSeries({ metric: 'rum.pageviews_by_quality', sites, dateFrom: fetchFrom, dateTo: range.to, dimension: 'quality' }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const qualityShape = useMemo(() => (quality.data
    ? sliceDays(shapeDaily(rowsAsSeriesByDimension(quality.data.rows), fetchFrom, range.to, 'rum.pageviews_by_quality'), range.from, range.to)
    : undefined), [quality.data, fetchFrom, range.from, range.to]);

  const queryFrom = addDays(range.to, -27);
  const queries = useQuery({
    queryKey: ['analytics-queries', queryFrom, range.to, site],
    queryFn: () => getMetricSeries({ metric: 'gsc.impressions', sites, dateFrom: queryFrom, dateTo: range.to, dimension: 'query' }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const stale = series.some((q) => q.isFetching) || crawlers.isFetching || queries.isFetching;
  const failed = series.find((q) => q.isError)?.error ?? catalog.error;

  // Full-window shapes keyed by metric, plus range-sliced (and optionally
  // combined) versions for the charts.
  const full = useMemo(() => {
    const out = new Map<string, DailyShape>();
    SERIES_KEYS.forEach((k, i) => {
      const data = series[i].data as SeriesResponse | undefined;
      if (data) out.set(k, shapeDaily(data.rows, fetchFrom, range.to, k));
    });
    return out;
  }, [series, fetchFrom, range.to]);

  const chart = (key: string): DailyShape | undefined => {
    const s = full.get(key);
    if (!s) return undefined;
    const sliced = sliceDays(s, range.from, range.to);
    return combine ? combineSites(sliced) : sliced;
  };

  // Headline row (§7.6). Bases are fixed by the guidance, not by the range.
  const pv30 = full.has('rum.pageviews') ? trailingTotal(full.get('rum.pageviews')!, 30) : undefined;
  const liImp30 = full.has('li.shares.impressions') ? trailingTotal(full.get('li.shares.impressions')!, 30) : undefined;
  const xImp30 = xShape ? trailingTotal(xShape, 30) : undefined;
  const ai7 = full.has('cf.ai_crawler.requests') ? trailingTotal(full.get('cf.ai_crawler.requests')!, 7) : undefined;
  // Hubs: the NETWORK figure is a distinct count, never the sum of the
  // per-site rows — a company covered on two mastheads has two hub pages but
  // is one company (validation note 2026-09-23; 3,281 summed vs 1,435 real).
  const hubsPerSite = full.has('internal.company_hubs') ? latestBySite(full.get('internal.company_hubs')!) : undefined;
  const hubs = full.has('internal.company_hubs_network') ? latestBySite(full.get('internal.company_hubs_network')!) : undefined;
  const branded30 = full.has('gsc.branded.impressions') ? trailingTotal(full.get('gsc.branded.impressions')!, 30) : undefined;
  const liGained30 = full.has('li.followers.gained') ? trailingTotal(full.get('li.followers.gained')!, 30) : undefined;
  const liFollowers = full.has('li.followers.total') ? latestBySite(full.get('li.followers.total')!) : undefined;
  // R1: beacon + LinkedIn (API) + X (Buffer) — the manual pull's definition.
  const contentParts = [pv30?.value, liImp30?.value, xImp30?.value];
  const contentViews = contentParts.some((v) => v != null)
    ? contentParts.reduce<number>((a, v) => a + (v ?? 0), 0)
    : null;
  // Trend badges (operator 2026-10-06): each headline window vs the window
  // before it. Content views compares the same three parts summed.
  const pvTrend = windowTrend(full.get('rum.pageviews'), 30);
  // Readers = page views − suspected crawler (operator 2026-10-13): SG /
  // Windows / no referrer, Tencent Cloud AS132203 — ~half of page views.
  const readersShape = full.has('rum.pageviews') && full.has('rum.crawler_suspect.pageviews')
    ? subtractShapes(full.get('rum.pageviews')!, full.get('rum.crawler_suspect.pageviews')!)
    : undefined;
  const readers30 = readersShape ? trailingTotal(readersShape, 30) : undefined;
  const crawler30 = full.has('rum.crawler_suspect.pageviews') ? trailingTotal(full.get('rum.crawler_suspect.pageviews')!, 30) : undefined;
  const readersTrend = windowTrend(readersShape, 30);
  const readersNote = readersTrend ? null : windowNoTrendReason(readersShape, 30);
  const aiTrend = windowTrend(full.get('cf.ai_crawler.requests'), 7);
  // 30-day view beside the 7-day one (Steve 2026-10-16). Same all-in series —
  // never filtered — read off the stored daily facts (collected since 2026-08-14).
  const ai30 = full.has('cf.ai_crawler.requests') ? trailingTotal(full.get('cf.ai_crawler.requests')!, 30) : undefined;
  const aiTrend30 = windowTrend(full.get('cf.ai_crawler.requests'), 30);
  const aiNote30 = aiTrend30 ? null : windowNoTrendReason(full.get('cf.ai_crawler.requests'), 30);
  const brandedTrend = windowTrend(full.get('gsc.branded.impressions'), 30);
  const liGainedTrend = windowTrend(full.get('li.followers.gained'), 30);
  const contentTrend = (() => {
    const shapes = [full.get('rum.pageviews'), full.get('li.shares.impressions'), xShape];
    if (shapes.some((s) => !s)) return null;
    const cur = shapes.map((s) => trailingTotal(s!, 30));
    const prev = shapes.map((s) => trailingTotal(s!, 30, 30));
    const sum = (ws: typeof cur) => ws.reduce((a, w) => a + (w.value ?? 0), 0);
    return trendOf(sum(cur), sum(prev), 'previous 30 days', prev.every((w) => w.daysWithData >= 30));
  })();
  const brandedNote = brandedTrend ? null : windowNoTrendReason(full.get('gsc.branded.impressions'), 30);
  const pvNote = pvTrend ? null : windowNoTrendReason(full.get('rum.pageviews'), 30);
  const aiNote = aiTrend ? null : windowNoTrendReason(full.get('cf.ai_crawler.requests'), 7);
  const liGainedNote = liGainedTrend ? null : windowNoTrendReason(full.get('li.followers.gained'), 30);
  const aiReads30 = full.has('cf.ai_assisted.reads') ? trailingTotal(full.get('cf.ai_assisted.reads')!, 30) : undefined;
  const aiReadsTrend = windowTrend(full.get('cf.ai_assisted.reads'), 30);
  const aiReadsNote = aiReadsTrend ? null : windowNoTrendReason(full.get('cf.ai_assisted.reads'), 30);
  const CATEGORY_LABEL: Record<string, string> = {
    answer: 'answers', agent_action: 'agent actions', notebook: 'notebook sources', read_aloud: 'read aloud',
  };
  const aiReadsCategoryLine = aiReadsCats.data
    ? topDimension(aiReadsCats.data.rows, 4)
        .map((c) => `${CATEGORY_LABEL[c.key] ?? c.key} ${Math.round(c.total).toLocaleString()}`)
        .join(' · ')
    : undefined;
  const hubsShape = full.get('internal.company_hubs_network');
  const hubsTrend = hubsShape?.through
    ? trendOf(hubs?.value, valueAsOf(hubsShape, addDays(hubsShape.through, -30)), 'count 30 days earlier',
        hubsShape.points.some((p) => p.date <= addDays(hubsShape.through!, -30)))
    : null;
  const hubsFirst = hubsShape?.points.find((p) => hubsShape.sites.some((s) => typeof p[s] === 'number'))?.date ?? null;
  const hubsNote = hubsTrend || !hubsShape?.through || hubs?.value == null ? null
    : hubsFirst && hubsFirst > addDays(hubsShape.through, -30)
      ? `no trend: hub history starts ${hubsFirst}, under 30 days`
      : 'no trend: the count 30 days earlier was 0';
  const through = Array.from(full.values()).reduce<string | null>((acc, s) => (s.through && (!acc || s.through > acc) ? s.through : acc), null);

  const crawlerRows = useMemo(() => (crawlers.data ? topDimension(crawlers.data.rows, 12) : []), [crawlers.data]);
  const crawlerSites = useMemo(() => Array.from(new Set((crawlers.data?.rows ?? []).map((r) => r.site))), [crawlers.data]);
  const queryRows = useMemo(() => (queries.data ? topDimension(queries.data.rows, 15) : []), [queries.data]);
  const querySites = useMemo(() => Array.from(new Set((queries.data?.rows ?? []).map((r) => r.site))), [queries.data]);

  const [downloading, setDownloading] = useState(false);
  async function onDownload() {
    setDownloading(true);
    try {
      const blob = await downloadNetworkCsv({ sites, dateFrom: range.from, dateTo: range.to });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `network-metrics_${range.from}_${range.to}${site === 'all' ? '' : `_${site}`}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const d = (k: string) => defs.get(k);

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
      <Helmet><title>Analytics · Assayer</title></Helmet>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-grey-900">Analytics</h1>
          <p className="text-sm text-grey-500">
            Network measurement · collected daily at 12:00 Sydney · {through ? `data through ${through}` : 'loading'}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownload}
          disabled={downloading}
          className="inline-flex items-center gap-2 rounded-full border border-grey-200 bg-surface px-4 py-2 text-sm font-medium text-grey-700 hover:bg-grey-50 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <Download className="h-4 w-4" />
          {downloading ? 'Preparing…' : 'Download the weekly dataset'}
        </button>
      </header>

      {/* ── Site scope: applies to the whole page, headline included ───── */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Site">
        <label className="flex items-center gap-2 text-xs text-grey-600">
          <span className="sr-only">Site</span>
          <select
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="rounded-full border border-grey-200 bg-surface px-3 py-1.5 text-xs font-medium text-grey-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            <option value="all">All sites</option>
            {SITE_ORDER.map((s) => <option key={s} value={s}>{SITE_LABEL[s]}</option>)}
          </select>
        </label>
      </div>

      {failed && (
        <div className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          The measurement API did not answer: {(failed as Error).message}
        </div>
      )}

      {/* ── Headline row (§7.6) ─────────────────────────────────────────── */}
      <h2 className="text-xs font-semibold uppercase tracking-wide text-grey-500 -mb-3">Headline · fixed windows, not affected by the chart range</h2>
      <section aria-label="Headline" className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 ${stale ? 'opacity-60' : ''} transition-opacity`}>
        <StatTile
          label="Content views"
          trend={contentTrend}
          value={contentViews}
          basis="rolling 30 days"
          visibility="public_safe"
          source={`beacon page views + LinkedIn impressions (LinkedIn API) + X impressions (Buffer)${through ? ` · through ${through}` : ''} · LinkedIn restated 2026-09-23: previously Buffer, which under-reported 29–50%`}
          sub={[
            pv30?.value != null ? `page views ${Math.round(pv30.value).toLocaleString()}` : null,
            liImp30?.value != null ? `LinkedIn ${Math.round(liImp30.value).toLocaleString()}` : null,
            xImp30?.value != null ? `X ${Math.round(xImp30.value).toLocaleString()}` : null,
          ].filter(Boolean).join(' · ') || undefined}
          estimatedDays={pv30?.estimatedDays ?? 0}
        />
        <StatTile
          label="Human page views"
          trend={pvTrend}
          trendNote={pvNote}
          value={pv30?.value}
          basis="rolling 30 days"
          visibility="public_safe"
          source={sourceLine(d('rum.pageviews'), pv30?.to ?? null)}
          sub={pv30 ? perSiteLine(pv30.bySite) : undefined}
          estimatedDays={pv30?.estimatedDays ?? 0}
          spark={full.has('rum.pageviews') ? sparkline(full.get('rum.pageviews')!, 12) : undefined}
        />
        <StatTile
          label="Readers (excl. suspected crawler)"
          trend={readersTrend}
          trendNote={readersNote}
          value={readers30?.value}
          basis="rolling 30 days"
          visibility="internal"
          source={crawler30?.value != null && pv30?.value
            ? `page views minus a Singapore headless crawler (Tencent Cloud): ${Math.round(crawler30.value).toLocaleString()} views, ${Math.round((crawler30.value / pv30.value) * 100)}% of the total`
            : 'suspected-crawler series not collected yet'}
          sub={readers30 ? perSiteLine(readers30.bySite) : undefined}
          estimatedDays={readers30?.estimatedDays ?? 0}
          spark={readersShape ? sparkline(readersShape, 12) : undefined}
        />
        <StatTile
          label="AI + AI-adjacent crawlers"
          trend={aiTrend}
          trendNote={aiNote}
          value={ai7?.value}
          basis="last 7 days"
          visibility="public_safe"
          source={sourceLine(d('cf.ai_crawler.requests'), ai7?.to ?? null)}
          sub={ai7 ? perSiteLine(ai7.bySite) : undefined}
          estimatedDays={ai7?.estimatedDays ?? 0}
          spark={full.has('cf.ai_crawler.requests') ? sparkline(full.get('cf.ai_crawler.requests')!, 12) : undefined}
        />
        <StatTile
          label="AI + AI-adjacent crawlers"
          trend={aiTrend30}
          trendNote={aiNote30}
          value={ai30?.value}
          basis="rolling 30 days"
          visibility="public_safe"
          source={sourceLine(d('cf.ai_crawler.requests'), ai30?.to ?? null)}
          sub={ai30 ? perSiteLine(ai30.bySite) : undefined}
          estimatedDays={ai30?.estimatedDays ?? 0}
          spark={full.has('cf.ai_crawler.requests') ? sparkline(full.get('cf.ai_crawler.requests')!, 30) : undefined}
        />
        <StatTile
          label="AI-assisted reads"
          value={aiReads30?.value}
          basis="rolling 30 days"
          visibility="public_safe"
          source={`Cloudflare · a person asked an AI or assistant and our page was fetched to serve it (ChatGPT, Claude, Perplexity, Mistral, DuckAssist, Meta, Google agents)${aiReads30?.to ? ` · through ${aiReads30.to}` : ''} · a floor: answers built from Google and Bing search indexes leave no separate fetch`}
          sub={aiReadsCategoryLine || (aiReads30 ? perSiteLine(aiReads30.bySite) : undefined)}
          estimatedDays={aiReads30?.estimatedDays ?? 0}
          spark={full.has('cf.ai_assisted.reads') ? sparkline(full.get('cf.ai_assisted.reads')!, 12) : undefined}
          trend={aiReadsTrend}
          trendNote={aiReadsNote}
        />
        <StatTile
          label="Branded search volume"
          trend={brandedTrend}
          trendNote={brandedNote}
          value={branded30?.value}
          basis="rolling 30 days"
          visibility="public_safe"
          source={`${sourceLine(d('gsc.branded.impressions'), branded30?.to ?? null)} · impressions on branded queries, of reported queries`}
          sub={branded30 ? perSiteLine(branded30.bySite) : undefined}
          estimatedDays={branded30?.estimatedDays ?? 0}
          spark={full.has('gsc.branded.impressions') ? sparkline(full.get('gsc.branded.impressions')!, 12) : undefined}
        />
        <StatTile
          label="New LinkedIn followers"
          trend={liGainedTrend}
          trendNote={liGainedNote}
          value={liGained30?.value}
          basis="rolling 30 days"
          visibility="public_safe"
          source={`${sourceLine(d('li.followers.gained'), liGained30?.to ?? null)} · organic + paid, reported to T-2`}
          sub={liFollowers?.value != null ? `followers ${perSiteLine(liFollowers.bySite)}` : liGained30 ? perSiteLine(liGained30.bySite) : undefined}
          spark={full.has('li.followers.gained') ? sparkline(full.get('li.followers.gained')!, 12) : undefined}
        />
        <StatTile
          label="Company hubs"
          trend={hubsTrend}
          trendNote={hubsNote}
          value={hubs?.value}
          basis="point in time"
          visibility="public_safe"
          source={`${sourceLine(d('internal.company_hubs_network'), hubs?.to ?? null)} · distinct companies with a hub on at least one masthead — per-site counts are not additive`}
          sub={hubsPerSite ? perSiteLine(hubsPerSite.bySite) : undefined}
        />
      </section>

      {/* ── Range: scopes the charts and tables below it — never the headline,
           whose windows are fixed by the guidance (§7.4, §7.6). ─────────── */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Chart range">
        <div className="flex items-center gap-1 rounded-full border border-grey-200 bg-surface p-1">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(p.key)}
              aria-pressed={preset === p.key}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-300 ${
                preset === p.key ? 'bg-primary-600 text-white' : 'text-grey-600 hover:bg-grey-100'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 rounded-full border border-grey-200 bg-surface px-3 py-1.5 text-xs font-medium text-grey-700 cursor-pointer">
          <input type="checkbox" checked={combine} onChange={(e) => setCombine(e.target.checked)} className="accent-primary-600" />
          Network roll-up
        </label>
        <span className="text-xs text-grey-400 ml-auto">{range.from} → {range.to}{preset === 'launch' ? ` (launch anchor ${LAUNCH_ANCHOR})` : ''}</span>
      </div>

      {/* ── Public-safe zone: leading beside lagging (§7.1, §7.3) ─────────── */}
      <section aria-label="Public-safe" className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-grey-500">Public-safe · the crawl → citation → visits sequence</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {chart('cf.ai_crawler.requests') && (
            <SeriesChart title="AI and AI-adjacent crawler requests" definition={d('cf.ai_crawler.requests')} {...cov('cf.ai_crawler.requests')} shape={chart('cf.ai_crawler.requests')!} note="Leading. Retrieval and training agents fetching pages, per day, classified by our own bot registry — wider than Cloudflare's AI Crawl Control list." source={sourceLine(d('cf.ai_crawler.requests'), null)} stale={stale} marker={{ date: REGISTRY_WIDENED, label: 'registry widened' }} />
          )}
          {chart('rum.pageviews') && (
            <SeriesChart title="Human page views" definition={d('rum.pageviews')} {...cov('rum.pageviews')} shape={chart('rum.pageviews')!} note="Lagging. Beacon page views with bots excluded — the truest readership figure." source={sourceLine(d('rum.pageviews'), null)} stale={stale} />
          )}
          {xShape && (
            <SeriesChart title="X impressions" definition={d('social.impressions')} {...cov('social.impressions')} shape={combine ? combineSites(sliceDays(xShape, range.from, range.to)) : sliceDays(xShape, range.from, range.to)} note="Leading. Impressions on the day's X posts, as Buffer reports them. LinkedIn impressions have their own chart, straight from LinkedIn." source={sourceLine(d('social.impressions'), null)} stale={stale || platform.isFetching} />
          )}
          {chart('li.followers.gained') && (
            <SeriesChart title="New LinkedIn followers" definition={d('li.followers.gained')} {...cov('li.followers.gained')} shape={chart('li.followers.gained')!} note="Leading. Followers gained per day on each masthead's LinkedIn Page, organic and paid. LinkedIn reports up to two days ago." source={sourceLine(d('li.followers.gained'), null)} stale={stale} />
          )}
          {chart('li.shares.impressions') && (
            <SeriesChart title="LinkedIn impressions" definition={d('li.shares.impressions')} {...cov('li.shares.impressions')} shape={chart('li.shares.impressions')!} note="Leading. Organic impressions on every post from each masthead's Page, per day, straight from LinkedIn." source={sourceLine(d('li.shares.impressions'), null)} stale={stale} />
          )}
          {chart('gsc.branded.impressions') && (
            <SeriesChart title="Branded search impressions" definition={d('gsc.branded.impressions')} {...cov('gsc.branded.impressions')} shape={chart('gsc.branded.impressions')!} note="Lagging. Impressions on queries that name a masthead or the network — a floor, counted from the queries Google reports. Days still being collected are drawn hollow." source={sourceLine(d('gsc.branded.impressions'), null)} stale={stale} />
          )}
        </div>
        <LinkedInFacets sites={sites} to={range.to} stale={stale} defs={defs} />
        <StandingFollowers to={range.to} />
      </section>

      {/* ── Internal zone (§7.1) ───────────────────────────────────────── */}
      <section aria-label="Internal" className="space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-grey-500">Internal · operational truth, never a headline</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {chart('rum.pageviews_au') && (
            <SeriesChart title="Human page views · Australia" definition={d('rum.pageviews_au')} {...cov('rum.pageviews_au')} shape={chart('rum.pageviews_au')!} note="The internal truth metric: the AU slice of the beacon, where offshore and synthetic traffic cannot flatter the number." source={sourceLine(d('rum.pageviews_au'), null)} stale={stale} />
          )}
          {chart('cf.zone.pageviews') && (
            <SeriesChart title="Zone page views (bot-inclusive)" definition={d('cf.zone.pageviews')} {...cov('cf.zone.pageviews')} shape={chart('cf.zone.pageviews')!} note="Cloudflare's own zone count, exact but IP-based and bot-inclusive — read beside the beacon, never instead of it." source={sourceLine(d('cf.zone.pageviews'), null)} stale={stale} />
          )}
          {chart('internal.articles_published') && (
            <SeriesChart title="Articles published" definition={d('internal.articles_published')} {...cov('internal.articles_published')} shape={chart('internal.articles_published')!} note="Successful publishes per day. A day with none is drawn as zero — the collector writes no row for it." source={sourceLine(d('internal.articles_published'), null)} stale={stale} />
          )}
          {chart('social.engagement') && (
            <SeriesChart title="Social reactions + comments" definition={d('social.engagement')} {...cov('social.engagement')} shape={chart('social.engagement')!} note={d('social.engagement')?.notes ?? 'Internal only: founder likes inflate these.'} source={sourceLine(d('social.engagement'), null)} stale={stale} />
          )}
          {chart('li.page.views') && (
            <SeriesChart title="LinkedIn Page views" definition={d('li.page.views')} {...cov('li.page.views')} shape={chart('li.page.views')!} note="Views of each masthead's LinkedIn Page (all sections, desktop and mobile), per day." source={sourceLine(d('li.page.views'), null)} stale={stale} />
          )}
          {chart('gsc.clicks') && (
            <SeriesChart title="Search clicks" definition={d('gsc.clicks')} {...cov('gsc.clicks')} shape={chart('gsc.clicks')!} note="Lagging. Clicks from Google web search per day. Days still being collected are drawn hollow and re-read until final." source={sourceLine(d('gsc.clicks'), null)} stale={stale} />
          )}
          {chart('gsc.impressions') && (
            <SeriesChart title="Search impressions" definition={d('gsc.impressions')} {...cov('gsc.impressions')} shape={chart('gsc.impressions')!} note="Lagging. Times a page was shown in Google web search per day — the denominator for clicks." source={sourceLine(d('gsc.impressions'), null)} stale={stale} />
          )}
        </div>
        {qualityShape && qualityShape.sites.length > 0 && (
          <SeriesChart
            title="Human page views by traffic quality"
            definition={d('rum.pageviews_by_quality')}
            {...cov('rum.pageviews_by_quality')}
            shape={qualityShape}
            note="Each country's page views classed by that country's own pages-per-visit that day: above 1.05 reads like people, at or below reads like single-page fetches. Diagnostic only — the published page-views figure counts both."
            source="Cloudflare beacon · derived from the per-country split"
            stale={stale || quality.isFetching}
          />
        )}
        {medianShape && medianShape.sites.length > 0 && (
          <SeriesChart
            title="Median impressions per LinkedIn post"
            definition={d('social.median_impressions_per_post')}
            shape={medianShape}
            note="What a typical post does, over the trailing 30 days — a median, so one viral post cannot set it. Never summed across mastheads."
            source={sourceLine(d('social.median_impressions_per_post'), null)}
            stale={stale || median.isFetching}
          />
        )}
        <TrafficQuality sites={sites} to={range.to} stale={stale} />
        <PipelineHealth sites={sites} to={range.to} stale={stale} />
        <PartnerClicks sites={sites} to={range.to} stale={stale} />
        <CrawlerTable rows={crawlerRows} sites={crawlerSites} from={crawlerFrom} to={range.to} stale={stale} />
        <CrawlerTable
          rows={queryRows}
          sites={querySites}
          from={queryFrom}
          to={range.to}
          stale={stale}
          title="Search queries"
          note={`Impressions by query, ${queryFrom} → ${range.to}. Branded queries carry the public-safe headline; the rest is internal.`}
          keyLabel="Query"
          valueLabel="Impressions"
          emptyText="No query rows in this window"
          footer="Search Console · web search · top 100 queries a day are stored; rare queries are anonymised at the source, so sums are a floor"
        />
      </section>

      {/* ── Reference: when each source's collection begins ─────────────── */}
      <CoverageTable rows={covFamilies} loading={coverage.isLoading} />
    </div>
  );
}
